/**
 * PlacePullService
 *
 * Thin HTTP client for the area-prospecting pull: start a job over a set of grid
 * cells, poll its progress, cancel it, and list the company's usable API keys for
 * a Google pull. The auth interceptor adds the bearer token automatically.
 */
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { PagedDto } from '../models/paged.dto';
import { environment } from '../../environment';
import { MapBounds } from '../map/core/spatial/map-viewport';
import { GridCell } from '../map/core/grid-object-selection';

export interface PullRequest {
  source: string;       // 'osm' | 'google'
  cells: GridCell[];    // each cell carries its own zoom
  apiKeyId?: number;    // google only
}

export interface PullStartResponse { jobId: string; }

export interface PullJobStatus {
  status: string;       // Queued | Running | Completed | Canceled | Failed
  cellsDone: number;
  cellsTotal: number;
  cellsFailed: number;
  upserted: number;
  runningCost: number;
}

export interface PullSummary extends PullJobStatus {
  jobId: string;
  source: string;
  cancelRequested: boolean;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  recordsAvailable: boolean;
  createsLeads: boolean;
  leadsCreated: number;
  leadsLinked: number;
  errorMessage: string | null;
}
export interface PullCell extends GridCell {
  cellIndex: number;
  status: string;
  newPlaces: number;
  updatedPlaces: number;
  errorMessage: string | null;
  startedAt: string | null;
  finishedAt: string | null;
}
export interface PullPlace extends GridCell {
  cellIndex: number;
  placeId: number;
  name: string | null;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  outcome: string;
  leadId: number | null;
  leadName: string | null;
  leadOutcome: string | null;
}
export interface PullCoverageCell extends GridCell { source: string; lastPulledAt: string; }
export interface PullCoverage { cells: PullCoverageCell[]; truncated: boolean; }

export type PullView = 'queue' | 'history' | 'all';
export const isPullActive = (job: PullJobStatus): boolean =>
  job.status === 'Queued' || job.status === 'Running';

export interface ApiKeyOption { id: number; name: string; }

@Injectable({ providedIn: 'root' })
export class PlacePullService {
  constructor(private http: HttpClient) { }

  startPull(req: PullRequest): Observable<PullStartResponse> {
    return this.http.post<PullStartResponse>(`${environment.apiUrl}/place/pull`, req);
  }

  getStatus(jobId: string): Observable<PullJobStatus> {
    return this.http.get<PullJobStatus>(`${environment.apiUrl}/place/pull/${jobId}`);
  }

  list(view: PullView, page: number, pageSize: number): Observable<PagedDto<PullSummary>> {
    return this.http.get<PagedDto<PullSummary>>(
      `${environment.apiUrl}/place/pull`, { params: { view, page, pageSize } });
  }

  details(jobId: string): Observable<PullSummary> {
    return this.http.get<PullSummary>(`${environment.apiUrl}/place/pull/${jobId}/details`);
  }

  cells(jobId: string, page: number, pageSize: number): Observable<PagedDto<PullCell>> {
    return this.http.get<PagedDto<PullCell>>(
      `${environment.apiUrl}/place/pull/${jobId}/cells`, { params: { page, pageSize } });
  }

  places(jobId: string, page: number, pageSize: number): Observable<PagedDto<PullPlace>> {
    return this.http.get<PagedDto<PullPlace>>(
      `${environment.apiUrl}/place/pull/${jobId}/places`, { params: { page, pageSize } });
  }

  coverage(bounds: MapBounds): Observable<PullCoverage> {
    return this.http.get<PullCoverage>(
      environment.apiUrl + '/place/pull/coverage', { params: { ...bounds } });
  }

  cancel(jobId: string): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/place/pull/${jobId}/cancel`, {});
  }

  getApiKeys(): Observable<ApiKeyOption[]> {
    return this.http.get<ApiKeyOption[]>(`${environment.apiUrl}/place/api-keys`);
  }
}
